import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  ChevronUp,
  CircleUserRound,
  History,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Monitor,
  Moon,
  Sun,
  Trash2,
  Users,
} from 'lucide-react'
import { collection, limit, orderBy, query, where } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  SectionTitle,
  SegmentedControl,
  Spinner,
} from '@/components/ui'
import { Modal } from '@/components/Modal'
import { useAuth } from '@/lib/auth'
import { useBooklets } from '@/hooks/useBooklets'
import { useMyCodes } from '@/hooks/useMyCodes'
import { useOffers } from '@/hooks/useOffers'
import { SavingsPanel } from '@/components/SavingsPanel'
import { useTheme, type ThemePreference } from '@/lib/theme'
import { useQuerySnapshot } from '@/hooks/useQuerySnapshot'
import { COLLECTIONS, ROLE_LABELS, hasAtLeastRole, type Redemption } from '@shared/model'
import { formatDate, formatDateTime, formatSek, toDate } from '@/lib/format'
import { deleteMyAccount } from '@/lib/api'
import { authErrorMessage } from '@/lib/authErrors'

/** Så många rader visas innan man ber om fler. */
const HISTORY_PREVIEW = 5

export function AccountPage() {
  const { user, profile, role, signOut } = useAuth()
  const { preference, setPreference } = useTheme()
  const navigate = useNavigate()

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleted, setDeleted] = useState(false)

  /**
   * Lämnar appen efter en radering.
   *
   * Utloggningen är inte en artighet utan det som faktiskt gör att man kommer
   * ut. Kontot är borta på servern, men webbläsaren har kvar sin session tills
   * någon säger till – och inloggningssidan skickar tillbaka den som ser
   * inloggad ut till startsidan. Utan signOut studsade man alltså rakt in i
   * appen igen, med ett konto som inte längre finns.
   */
  async function leave() {
    await signOut()
    navigate('/logga-in', { replace: true })
  }

  const historyQuery = useMemo(() => {
    if (!user) return null
    return query(
      collection(db, COLLECTIONS.redemptions),
      where('userUid', '==', user.uid),
      orderBy('redeemedAt', 'desc'),
      limit(50),
    )
  }, [user])

  const { data: history, loading: historyLoading } = useQuerySnapshot<Redemption>(historyQuery)

  /*
   * Bara de senaste fem syns från början.
   *
   * Ett välanvänt häfte ger trettio rader, och listan sköt då ned allt under
   * sig – utloggningen och kontoraderingen hamnade en halv skärm bort på
   * telefonen. De fem senaste svarar på frågan man faktiskt har ("använde jag
   * den där redan?"), resten är historik man söker upp när man vill.
   */
  const [showAllHistory, setShowAllHistory] = useState(false)
  const visibleHistory = showAllHistory ? history : history.slice(0, HISTORY_PREVIEW)

  /*
   * Underlaget till sparpanelen.
   *
   * Panelen visar den period man faktiskt har ett aktiverat häfte i. Äger man
   * flera väljs det pågående – det är där siffran fortfarande rör sig, och
   * en panel som visar en avslutad period medan en pågår vore missvisande.
   */
  const { booklets } = useBooklets()
  const { codes } = useMyCodes()

  const activeBooklet = useMemo(() => {
    const mine = booklets.filter((booklet) =>
      codes.some((code) => code.bookletId === booklet.id && code.status === 'activated'),
    )
    return mine.find((booklet) => booklet.status === 'active') ?? mine[0] ?? null
  }, [booklets, codes])

  const { data: activeOffers } = useOffers(activeBooklet?.id ?? null)

  const activeSaved = activeBooklet
    ? (profile?.savingsByBooklet?.[activeBooklet.id]?.totalSek ?? 0)
    : 0
  const activeRedemptions = activeBooklet
    ? (profile?.savingsByBooklet?.[activeBooklet.id]?.redemptionCount ?? 0)
    : 0
  const activeBookletValue = useMemo(
    () => activeOffers.reduce((sum, offer) => sum + (offer.estimatedValueSek ?? 0), 0),
    [activeOffers],
  )

  const lifetimeSaved = useMemo(
    () =>
      Object.values(profile?.savingsByBooklet ?? {}).reduce(
        (sum, entry) => sum + entry.totalSek,
        0,
      ),
    [profile],
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-extrabold">Mitt konto</h1>
      </div>

      <Card className="flex items-center gap-4 p-5">
        <CircleUserRound size={44} className="shrink-0 text-brand-ink" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display font-extrabold text-ink">{user?.email}</p>
          <Badge tone="brand" className="mt-1">
            {ROLE_LABELS[role]}
          </Badge>
        </div>
      </Card>

      {/*
       * Sparpanelen bor här sedan häftets förstasida gjordes om.
       *
       * Nivåer, mätare och "så här nära nästa nivå" är något man tittar på
       * efteråt, inte medan man letar efter en rabatt i en butik. På
       * förstasidan står numera bara beloppet som en rad, och den som vill se
       * hela bilden är ett tryck bort – här.
       */}
      {activeBooklet && (
        <SavingsPanel
          booklet={activeBooklet}
          totalSavedSek={activeSaved}
          redemptionCount={activeRedemptions}
          offerCount={activeOffers.length}
          bookletValueSek={activeBookletValue}
        />
      )}

      {lifetimeSaved > 0 && (
        <Card className="bg-brand-surface p-5 text-on-brand">
          <p className="font-display text-xs font-bold tracking-[0.16em] text-accent uppercase">
            Totalt sparat hos oss
          </p>
          <p className="mt-1 font-display text-4xl font-extrabold">{formatSek(lifetimeSaved)}</p>
        </Card>
      )}

      {/* Genvägar för dem som har fler roller än köpare. */}
      {(hasAtLeastRole(role, 'teamLeader') || hasAtLeastRole(role, 'admin')) && (
        <Card className="divide-y divide-border p-0">
          {hasAtLeastRole(role, 'teamLeader') && (
            <Link to="/lag" className="flex items-center gap-3 p-4 hover:bg-surface-2">
              <Users size={20} className="text-brand-ink" aria-hidden />
              <span className="font-semibold">Mitt lag</span>
            </Link>
          )}
          {hasAtLeastRole(role, 'admin') && (
            <Link to="/admin" className="flex items-center gap-3 p-4 hover:bg-surface-2">
              <LayoutDashboard size={20} className="text-brand-ink" aria-hidden />
              <span className="font-semibold">Administration</span>
            </Link>
          )}
        </Card>
      )}

      <Card className="p-5">
        <SectionTitle>Utseende</SectionTitle>
        <SegmentedControl<ThemePreference>
          label="Färgtema"
          value={preference}
          onChange={setPreference}
          className="w-full"
          options={[
            {
              value: 'system',
              label: (
                <span className="flex items-center gap-1.5">
                  <Monitor size={15} aria-hidden />
                  System
                </span>
              ),
            },
            {
              value: 'light',
              label: (
                <span className="flex items-center gap-1.5">
                  <Sun size={15} aria-hidden />
                  Ljust
                </span>
              ),
            },
            {
              value: 'dark',
              label: (
                <span className="flex items-center gap-1.5">
                  <Moon size={15} aria-hidden />
                  Mörkt
                </span>
              ),
            },
          ]}
        />
      </Card>

      <Card className="p-5">
        <SectionTitle>
          <span className="flex items-center gap-2">
            <History size={18} aria-hidden />
            Använda rabatter
          </span>
        </SectionTitle>

        {historyLoading ? (
          <Spinner />
        ) : history.length === 0 ? (
          <EmptyState
            title="Inga rabatter använda än"
            description="När du löser in ett erbjudande hamnar det här."
          />
        ) : (
          <ul className="divide-y divide-border">
            {visibleHistory.map((redemption) => (
              <li key={redemption.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink">{redemption.offerTitle}</p>
                  <p className="truncate text-sm text-muted">{redemption.companyName}</p>
                  <p className="text-xs text-faint">{formatDateTime(redemption.redeemedAt)}</p>
                </div>
                <div className="shrink-0 text-right">
                  {redemption.revoked ? (
                    <Badge tone="warning">Återställd</Badge>
                  ) : (
                    <span className="font-display font-extrabold text-success">
                      {formatSek(redemption.valueSek)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Knappen säger hur många som döljs. "Visa fler" utan siffra tvingar
            en att trycka för att få veta om det är två rader till eller
            tjugofem. */}
        {!showAllHistory && history.length > HISTORY_PREVIEW && (
          <Button
            variant="quiet"
            fullWidth
            className="mt-3"
            icon={<ChevronDown size={16} />}
            onClick={() => setShowAllHistory(true)}
          >
            Visa {history.length - HISTORY_PREVIEW} till
          </Button>
        )}

        {showAllHistory && history.length > HISTORY_PREVIEW && (
          <Button
            variant="quiet"
            fullWidth
            className="mt-3"
            icon={<ChevronUp size={16} />}
            onClick={() => setShowAllHistory(false)}
          >
            Visa färre
          </Button>
        )}
      </Card>

      <Card className="divide-y divide-border p-0">
        <Link to="/hjalp" className="flex items-center gap-3 p-4 hover:bg-surface-2">
          <LifeBuoy size={20} className="text-brand-ink" aria-hidden />
          <span className="font-semibold">Hjälp &amp; kontakt</span>
        </Link>
        <button
          type="button"
          onClick={() => void signOut().then(() => navigate('/logga-in'))}
          className="flex w-full items-center gap-3 p-4 text-left hover:bg-surface-2"
        >
          <LogOut size={20} className="text-brand-ink" aria-hidden />
          <span className="font-semibold">Logga ut</span>
        </button>
      </Card>

      <MyDataCard redemptionCount={history.length} />

      {/* Rätten att bli glömd. Medvetet längst ned och i egen ruta. */}
      <Card className="border-danger/30 p-5">
        <SectionTitle>Radera kontot</SectionTitle>
        <p className="mb-4 text-sm text-muted">
          Du kan när som helst radera ditt konto och dina uppgifter. Häften du aktiverat slutar
          gälla och kan inte återaktiveras. Föreningen behåller bara anonym statistik om hur många
          rabatter som använts totalt.{' '}
          <Link to="/integritet" className="font-semibold text-brand-ink underline">
            Så hanterar vi dina uppgifter
          </Link>
          .
        </p>
        <Button variant="danger" icon={<Trash2 size={17} />} onClick={() => setDeleteOpen(true)}>
          Radera mitt konto
        </Button>
      </Card>

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false)
          setDeleted(true)
        }}
      />

      {deleted && <AccountDeletedModal onLeave={() => void leave()} />}
    </div>
  )
}

/**
 * Kvittensen efter en radering.
 *
 * En toast dög inte. Raderingen är det mest oåterkalleliga en användare kan
 * göra i appen, och beskedet om att den gick igenom ska stå kvar tills man
 * tagit det till sig – inte glida förbi i ett hörn medan sidan bakom ändå
 * håller på att tömmas.
 *
 * Rutan går bara att lämna åt ett håll, och det är ut. Därför samma handling
 * på krysset som på knappen.
 */
function AccountDeletedModal({ onLeave }: { onLeave: () => void }) {
  return (
    <Modal
      open
      onClose={onLeave}
      title="Kontot är raderat"
      size="sm"
      footer={
        <Button fullWidth onClick={onLeave}>
          Till inloggningen
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ink">
          Ditt konto och dina uppgifter är borttagna. Inlösta rabatter ligger kvar i föreningens
          statistik, men utan koppling till dig.
        </p>
        <p className="text-sm text-muted">Tack för att du stöttat Lira BK!</p>
      </div>
    </Modal>
  )
}

/**
 * Allt föreningen har om dig, på en skärm.
 *
 * Dataskyddsförordningen ger rätt att få veta vilka uppgifter som finns och
 * få en kopia. Det brukar lösas med en begäran, en månads handläggning och en
 * fil – men när uppgifterna är så här få är det en omväg. Här står de i
 * stället framme, alltid, utan att någon behöver fråga.
 *
 * Kortet räknar inte upp sådant som redan syns ovanför eller under Mina
 * koder. Det fyller luckorna: när kontot skapades, när det senast användes,
 * och de felrapporter man skickat – de tre enda sakerna som annars inte går
 * att se själv.
 *
 * Listan måste hållas i takt med vad appen faktiskt sparar. Står det här att
 * det är allt, och det inte är allt, är det värre än att inte säga något.
 */
function MyDataCard({ redemptionCount }: { redemptionCount: number }) {
  const { user, profile } = useAuth()

  /*
   * Utan orderBy, med flit: en fråga med både villkor och sortering kräver
   * ett sammansatt index, och ingen har fler felrapporter än att de sorteras
   * billigare här.
   */
  const reportsQuery = useMemo(() => {
    if (!user) return null
    return query(collection(db, COLLECTIONS.reports), where('userUid', '==', user.uid))
  }, [user])

  const { data: reports } = useQuerySnapshot<{
    id: string
    type: string
    message: string
    createdAt: { toDate(): Date } | null
  }>(reportsQuery)

  const sortedReports = useMemo(
    () =>
      [...reports].sort(
        (a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0),
      ),
    [reports],
  )

  return (
    <Card className="p-5">
      <SectionTitle>Allt vi har om dig</SectionTitle>
      <p className="mb-4 text-sm text-muted">
        Du behöver inte begära ut något. Det här är hela listan, och den står framme.
      </p>

      <dl className="divide-y divide-border text-sm">
        <div className="flex items-baseline justify-between gap-3 py-2">
          <dt className="text-muted">E-postadress</dt>
          <dd className="text-right font-semibold break-all">{user?.email}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-2">
          <dt className="text-muted">Kontot skapades</dt>
          <dd className="text-right font-semibold">{formatDate(profile?.createdAt)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-2">
          <dt className="text-muted">Senast inloggad</dt>
          <dd className="text-right font-semibold">{formatDate(profile?.lastActiveAt)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-2">
          <dt className="text-muted">Rabatter du använt</dt>
          <dd className="text-right font-semibold">
            {redemptionCount} st, listade ovanför
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-2">
          <dt className="text-muted">Häften du aktiverat</dt>
          <dd className="text-right font-semibold">
            <Link to="/mina-koder" className="text-brand-ink underline">
              Mina koder
            </Link>
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-2">
          <dt className="text-muted">Felrapporter du skickat</dt>
          <dd className="text-right font-semibold">{sortedReports.length} st</dd>
        </div>
      </dl>

      {sortedReports.length > 0 && (
        <ul className="mt-3 space-y-2">
          {sortedReports.map((report) => (
            <li key={report.id} className="rounded-xl bg-surface-2 p-3 text-sm">
              <p className="text-xs text-muted">{formatDateTime(report.createdAt)}</p>
              <p className="mt-0.5 line-clamp-3">{report.message}</p>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 text-sm text-muted">
        Utöver det här sparar vi ingenting om dig.{' '}
        <Link to="/integritet" className="font-semibold text-brand-ink underline">
          Så hanterar vi dina uppgifter
        </Link>
        .
      </p>
    </Card>
  )
}

function DeleteAccountModal({
  open,
  onClose,
  onDeleted,
}: {
  open: boolean
  onClose: () => void
  onDeleted: () => void
}) {
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const ready = confirmation.trim().toUpperCase() === 'RADERA'

  async function handleDelete() {
    setError(null)
    setBusy(true)
    try {
      await deleteMyAccount({ confirm: 'RADERA' })
      onDeleted()
    } catch (caught) {
      setError(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Radera kontot"
      size="sm"
      footer={
        <div className="flex gap-2">
          <Button variant="quiet" fullWidth onClick={onClose}>
            Avbryt
          </Button>
          <Button
            variant="danger"
            fullWidth
            disabled={!ready}
            loading={busy}
            onClick={() => void handleDelete()}
          >
            Radera
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <p className="text-sm text-muted">
          Det här går inte att ångra. Skriv <strong className="text-ink">RADERA</strong> för att
          bekräfta.
        </p>
        <Field label="Bekräftelse">
          {({ id }) => (
            <Input
              id={id}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="off"
              placeholder="RADERA"
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}
